import { z } from "zod";

// =============================================================
// Module local « x-atelier » (PROTECTO) : clients et véhicules, catalogue de
// services par catégorie, techniciens, rendez-vous (travaux) avec lignes de
// services, agenda et statuts. Types partagés API ↔ web + schémas zod.
// =============================================================

export type ClientType = "particulier" | "concessionnaire" | "flotte";
/**
 * « Nouveau » tant qu'aucun de ses travaux n'est terminé (réservé en ligne, par
 * Léo ou au CRM sans être encore venu) ; « confirmé » dès le premier travail
 * terminé. Calculé d'après les rendez-vous, jamais saisi.
 */
export type ClientStatus = "nouveau" | "confirme";
export type LeadSource =
  | "urable"
  | "site_web"
  | "chatbot"
  | "appel_ia"
  | "appel_direct"
  | "campagne_sms"
  | "reference"
  | "concessionnaire"
  | "reseaux_sociaux"
  | "autre";
export type AppointmentStatus =
  | "devis"
  | "reserve"
  | "confirme"
  | "vehicule_recu"
  | "en_cours"
  | "termine"
  | "pret"
  | "recupere"
  | "annule"
  | "non_presente";
/**
 * Canal par lequel le rendez-vous a été pris, fixé à la création et jamais
 * modifié ensuite : distinct du statut (où en est le travail) et de la source
 * (provenance commerciale du client, modifiable au CRM).
 */
export type BookingChannel = "crm" | "site_web" | "chatbot" | "appel_ia" | "urable";
export type PaymentMethod = "carte" | "comptant" | "virement" | "en_ligne" | "autre";
export type PaymentKind = "acompte" | "solde" | "remboursement";

export interface Client {
  id: string;
  type: ClientType;
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  /** Consentement aux SMS promotionnels (distinct du transactionnel). */
  smsConsent: boolean;
  source: LeadSource;
  notes: string | null;
  createdAt: number | string;
  updatedAt: number | string;
  /** Calculé par l'API (liste et fiche) : voir `ClientStatus`. */
  status?: ClientStatus;
  /** Fin du premier travail terminé ; null pour un nouveau client. */
  confirmedAt?: number | string | null;
}

export interface Vehicle {
  pricingClass?: VehicleClass | null;
  id: string;
  clientId: string;
  make: string;
  model: string;
  year: number | null;
  /** Numéro d'identification du véhicule (17 caractères). */
  vin: string | null;
  plate: string | null;
  mileage: number | null;
  color: string | null;
  notes: string | null;
  createdAt: number | string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  position: number;
  /** Visible sur le site et réservable en ligne. */
  onlineVisible: boolean;
  /** Couleur des rendez-vous de cette catégorie dans l'agenda ; null = couleur par défaut de son rang. */
  color?: string | null;
}

/**
 * Couleurs d'agenda proposées : des teintes qui restent distinctes même pour un
 * œil qui confond le rouge et le vert (elles diffèrent aussi en clarté). Une
 * catégorie sans couleur choisie prend celle de son rang, pour qu'un catalogue
 * existant soit déjà lisible sans le moindre réglage.
 *
 * L'ordre suit le vocabulaire de l'atelier : bleu = esthétique, jaune =
 * antirouille, orange = forfait (antirouille + lavage), rouge = annulé ou
 * absent.
 */
export const CATEGORY_COLORS = [
  "#2563eb",
  "#f5b800",
  "#ea580c",
  "#dc2626",
  "#16a34a",
  "#0891b2",
  "#7c3aed",
  "#db2777",
  "#475569",
] as const;

/** Rouge des rendez-vous annulés et des absences, quelle que soit la catégorie. */
export const INACTIVE_COLOR = "#dc2626";

/** Couleur d'une catégorie : la sienne, sinon celle de son rang. */
export function categoryColor(c: { color?: string | null; position?: number }): string {
  if (c.color && isHexColor(c.color)) return c.color;
  const index = Math.max(0, c.position ?? 0) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[index]!;
}

/** « #a1b2c3 » ou « #abc » — seule forme acceptée en base et dans les champs. */
export function isHexColor(value: string): boolean {
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}

export const VEHICLE_CLASSES = [
  "coupe",
  "sedan",
  "suv",
  "large_suv",
  "truck",
  "large_truck",
] as const;
export type VehicleClass = (typeof VEHICLE_CLASSES)[number];
export const VEHICLE_CLASS_LABELS: Record<VehicleClass, string> = {
  coupe: "Coupé",
  sedan: "Berline",
  suv: "VUS",
  large_suv: "Grand VUS",
  truck: "Camionnette",
  large_truck: "Gros camion",
};
/** Mapping exact des libellés source ; les options de sièges/tapis restent indépendantes. */
export function vehicleClassesForVariant(label: string): VehicleClass[] {
  const known: Record<string, VehicleClass[]> = {
    Coupe: ["coupe"],
    Sedan: ["sedan"],
    SUV: ["suv"],
    "Large SUV": ["large_suv"],
    Truck: ["truck"],
    Camion: ["truck"],
    "Gros Camion": ["large_truck"],
    "Sedan/Vus": ["sedan", "suv"],
  };
  return known[label.trim()] ?? [];
}
/** Prix fixe, ou tarif horaire (`priceCents` = le taux pour une heure, montant final selon le temps). */
export type PricingUnit = "fixed" | "hourly";

export const PRICING_UNIT_LABELS: Record<PricingUnit, string> = {
  fixed: "Prix fixe",
  hourly: "À l’heure",
};

/**
 * Liaison entre deux services : « obligatoire » = le service lié s'ajoute de
 * lui-même au rendez-vous ; « recommandé » = Léo et les écrans le proposent.
 * Une liaison vaut pour toutes les déclinaisons des deux services (même
 * groupe de variantes) ; `vehicleClasses` la restreint à certains véhicules
 * (« le châssis n'est pas inclus » pour une camionnette seulement).
 */
export type ServiceLinkKind = "obligatoire" | "recommande";

export const SERVICE_LINK_KIND_LABELS: Record<ServiceLinkKind, string> = {
  obligatoire: "Obligatoire (ajouté automatiquement)",
  recommande: "Recommandé (proposé)",
};

export interface ServiceLink {
  id: string;
  serviceId: string;
  linkedServiceId: string;
  kind: ServiceLinkKind;
  /** Précision dite au client (« nécessaire dans 95 % des cas », « le châssis n'est pas inclus »). */
  note: string | null;
  /** Vide : tous les véhicules. */
  vehicleClasses: VehicleClass[];
  position: number;
}

export interface Service {
  warrantyEligible?: boolean;
  vehicleClasses?: VehicleClass[] | null;
  variantGroup?: string | null;
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  /** Prix fixe en cents ; null si sur devis. Au tarif horaire : le taux d'une heure. */
  priceCents: number | null;
  /** Prix fixe (défaut) ou tarif horaire. */
  pricingUnit?: PricingUnit;
  /** Réservé aux véhicules de tant d'années ou moins (antirouille permanent : 2) ; null = sans limite. */
  maxVehicleAgeYears?: number | null;
  /** Service de transport offert dans ce rayon (km) autour de l'atelier ; null = pas un transport. */
  maxDistanceKm?: number | null;
  /** Services liés (obligatoires ou recommandés), portés par le service et ses déclinaisons. */
  links?: ServiceLink[];
  quoteOnly: boolean;
  /** null : durée à préciser avant de planifier le service. */
  durationMin: number | null;
  onlineBookable: boolean;
  /** Consultable sur le site, même si une confirmation de l'atelier est nécessaire. */
  publicVisible?: boolean;
  /** L'agent IA peut communiquer ce prix. */
  aiPriceVisible: boolean;
  active: boolean;
  position: number;
  /** Temps de main-d'œuvre estimé en minutes ; null = durée du service. */
  laborMin: number | null;
  /** Slugs des guides (procédures) liés au service. */
  guideSlugs: string[];
  /**
   * Forfait : identifiants des services qu'il regroupe (vide = service
   * simple). Quand ces services sont cédulés séparément, l'écran propose le
   * forfait à la place s'il coûte moins cher.
   */
  bundleServiceIds: string[];
  /** Coût de revient en cents — présent seulement pour un admin (GET /catalogue). */
  costCents?: number | null;
  /** Marge en % du prix (une décimale) ; null si sur devis — admin seulement. */
  marginPct?: number | null;
}

/** Produit consommé par les services (pellicule, teinte, produit antirouille…). */
export interface Product {
  id: string;
  name: string;
  supplier: string | null;
  /** Unité de mesure de la quantité : « ml », « L », « unité »… */
  unit: string;
  /** Coût par unité en cents — renvoyé uniquement aux administrateurs. */
  unitCostCents?: number;
  active: boolean;
  position: number;
}

/** Un produit consommé par un service, avec la quantité par prestation. */
export interface ServiceProduct {
  productId: string;
  name: string;
  unit: string;
  quantity: number;
  /** Admin seulement. */
  unitCostCents?: number;
}

export interface ServiceCostLine {
  productId: string;
  name: string;
  unit: string;
  quantity: number;
  unitCostCents: number;
  costCents: number;
}

/** Coût de revient d'un service (admin) : produits + main-d'œuvre, prix et marge. */
export interface ServiceCost {
  serviceId: string;
  priceCents: number | null;
  /** Minutes de main-d'œuvre retenues (laborMin, sinon durationMin). */
  laborMin: number | null;
  /** Taux horaire moyen des techniciens actifs ayant un taux (0 si aucun). */
  hourlyRateCents: number;
  laborCents: number | null;
  products: ServiceCostLine[];
  productsCents: number;
  costCents: number | null;
  /** null si le service est sur devis. */
  marginCents: number | null;
  marginPct: number | null;
}

export type WeeklyHours = Record<string, { open: string; close: string }[]>;

export interface Technician {
  id: string;
  /** Compte lié (facultatif : un technicien peut ne pas avoir d'accès). */
  userId: string | null;
  displayName: string;
  /** Couleur d'agenda (#rrggbb). */
  color: string;
  phone: string | null;
  /** Taux horaire en cents — renvoyé uniquement aux administrateurs. */
  hourlyRateCents?: number | null;
  active: boolean;
  position: number;
  /** Jours de présence (1 = lundi … 7 = dimanche). */
  workdays: number[];
  weeklyHours?: WeeklyHours | null;
}

/** Absence d'un technicien : bornes « AAAA-MM-JJ » inclusives (jour de l'atelier). */
export interface TimeOff {
  id: string;
  technicianId: string;
  fromDate: string;
  toDate: string;
  fromTime?: string | null;
  toTime?: string | null;
  label: string | null;
  createdAt: number | string;
}

/** Lundi → vendredi : la présence par défaut. */
export const DEFAULT_WORKDAYS = [1, 2, 3, 4, 5];

export const WEEKDAY_SHORT_LABELS: Record<number, string> = {
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Jeu",
  5: "Ven",
  6: "Sam",
  7: "Dim",
};

export interface AppointmentLine {
  id: string;
  appointmentId: string;
  serviceId: string | null;
  label: string;
  /** Prix unitaire AVANT rabais. */
  priceCents: number;
  durationMin: number;
  quantity: number;
  /** Rabais en % (0–100) ; 100 = offert. */
  discountPct: number;
  /** Nom de la règle qui a posé le rabais ; null = rabais saisi à la main. */
  discountLabel: string | null;
}

/**
 * Règle de rabais automatique : quand l'un des services déclencheurs est au
 * rendez-vous, chacun des services cibles présents reçoit le pourcentage.
 * Ex. « Antirouille → protection extérieure à −30 % ».
 */
export interface DiscountRule {
  id: string;
  name: string;
  percent: number;
  triggerServiceIds: string[];
  targetServiceIds: string[];
  active: boolean;
  position: number;
}

export interface Appointment {
  id: string;
  clientId: string;
  vehicleId: string | null;
  technicianId: string | null;
  status: AppointmentStatus;
  /** Début/fin en secondes Unix (API) ou ISO (REST). */
  startAt: number | string;
  endAt: number | string;
  /** Aucun créneau fixé : ne pas afficher startAt/endAt comme un rendez-vous. */
  schedulePending?: boolean;
  /** Titre affiché : « 2023 Kia Seltos EX » ou le nom du service. */
  title: string;
  /** Notes internes : jamais lues au client (site, textos, assistant téléphonique). */
  notes: string | null;
  /** Consignes destinées au client (lues par Léo, reprises dans les échanges). */
  clientInstructions?: string | null;
  source: LeadSource;
  /** Canal de prise du rendez-vous, conservé après toute modification. */
  bookingChannel: BookingChannel;
  /** Le client a confirmé sa présence (téléphone, CRM) ; null = pas encore. */
  presenceConfirmedAt?: number | string | null;
  /** Sous-total HORS taxes = Σ lignes (les taxes sont calculées dans la vue). */
  totalCents: number;
  /** Dérivé : Σ des paiements de type acompte (dès qu'un paiement existe). */
  depositCents: number;
  /** Dérivé : acomptes + soldes − remboursements (dès qu'un paiement existe). */
  paidCents: number;
  /** Numéro de facture « F-2026-0001 », attribué à la première facturation. */
  invoiceNumber: string | null;
  invoicedAt: number | string | null;
  /** Numéro de devis « D-2026-0001 ». */
  quoteNumber: string | null;
  quotedAt: number | string | null;
  /** Exonéré de TPS/TVQ (client hors Québec, revente…). */
  taxExempt: boolean;
  /** Couleur forcée pour ce rendez-vous ; null = celle de la catégorie du premier service. */
  color?: string | null;
  createdAt: number | string;
  updatedAt: number | string;
  createdByUserId: string | null;
}

/** Encaissement (ou remboursement) rattaché à un rendez-vous. */
export interface Payment {
  id: string;
  appointmentId: string;
  amountCents: number;
  method: PaymentMethod;
  kind: PaymentKind;
  paidAt: number | string;
  /** Numéro de transaction, de chèque… (réservé aussi à l'identifiant Stripe à venir). */
  reference: string | null;
  notes: string | null;
  createdAt: number | string;
  createdByUserId: string | null;
}

export interface AppointmentTaxes {
  gstCents: number;
  qstCents: number;
  /** Taxes historiques totales ; la source ne fournit pas leur ventilation. */
  importedTotalCents?: number;
}

/** Rendez-vous enrichi pour l'agenda et les listes. */
export interface AppointmentView extends Appointment {
  clientName: string;
  clientPhone: string | null;
  vehicleLabel: string | null;
  technicianName: string | null;
  technicianColor: string | null;
  /**
   * Couleur à afficher, déjà résolue par l'API : celle du rendez-vous, sinon
   * celle de la catégorie de son premier service, sinon celle du technicien.
   */
  colorHex?: string | null;
  /** Catégorie ayant donné la couleur — sert de légende sur la carte. */
  categoryName?: string | null;
  /** Le client n'a encore aucun travail terminé (première visite) : « nouveau ». */
  clientStatus?: ClientStatus;
  lines: AppointmentLine[];
  /** TPS et TVQ sur le sous-total (zéro si exonéré). */
  taxes: AppointmentTaxes;
  totalWithTaxCents: number;
  /** Solde dû = total TTC − payé (jamais négatif). */
  dueCents: number;
  payments: Payment[];
}

export interface ClientDetail {
  client: Client;
  vehicles: Vehicle[];
  appointments: AppointmentView[];
}

export interface CategoryWithServices extends ServiceCategory {
  services: Service[];
}

export interface AtelierDayStats {
  date: string;
  appointmentCount: number;
  totalCents: number;
  durationMin: number;
}

// ---- Libellés ----

export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  particulier: "Particulier",
  concessionnaire: "Concessionnaire",
  flotte: "Flotte / entreprise",
};

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  urable: "Urable",
  site_web: "Site web",
  chatbot: "Chatbot",
  appel_ia: "Appel IA",
  appel_direct: "Appel direct",
  campagne_sms: "Campagne SMS",
  reference: "Référence",
  concessionnaire: "Concessionnaire",
  reseaux_sociaux: "Réseaux sociaux",
  autre: "Autre",
};

export const BOOKING_CHANNEL_LABELS: Record<BookingChannel, string> = {
  crm: "Pris au CRM",
  site_web: "Réservé en ligne",
  chatbot: "Réservé en ligne (assistant du site)",
  appel_ia: "Réservé par Léo au téléphone",
  urable: "Importé d’Urable",
};

/** Pris par le client lui-même sur le site (formulaire ou assistant) : badge « Réservé en ligne ». */
export function isOnlineBooking(channel: BookingChannel | null | undefined): boolean {
  return channel === "site_web" || channel === "chatbot";
}

/** Pris par un assistant IA (téléphone ou site) : confirmation par texto avec accord seulement. */
export function isAssistantBooking(channel: BookingChannel | null | undefined): boolean {
  return channel === "appel_ia" || channel === "chatbot";
}

/** Entrée du journal d'un rendez-vous (création, présence, dérogation, lien avec une demande…). */
export interface AppointmentEvent {
  id: string;
  appointmentId: string;
  kind: AppointmentEventKind;
  description: string;
  actorName: string | null;
  createdAt: number | string;
}
export type AppointmentEventKind =
  "creation" | "statut" | "horaire" | "presence" | "derogation_pause" | "demande_site" | "note";

/**
 * Statuts depuis lesquels une confirmation de présence peut faire passer le
 * rendez-vous à « Confirmé ». Un rendez-vous annulé, terminé, en cours ou déjà
 * à l'atelier n'y revient jamais ; un devis n'est pas encore un rendez-vous.
 */
export const PRESENCE_CONFIRMABLE_STATUSES: AppointmentStatus[] = ["reserve", "confirme"];

/** Travail achevé : le premier fait passer le client de « nouveau » à « confirmé ». */
export const COMPLETED_APPOINTMENT_STATUSES: AppointmentStatus[] = ["termine", "pret", "recupere"];

export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  nouveau: "Nouveau client",
  confirme: "Client confirmé",
};

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  devis: "Devis",
  reserve: "Réservé",
  confirme: "Confirmé",
  vehicule_recu: "Véhicule reçu",
  en_cours: "En cours",
  termine: "Terminé",
  pret: "Prêt à récupérer",
  recupere: "Récupéré",
  annule: "Annulé",
  non_presente: "Non présenté",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  carte: "Carte",
  comptant: "Comptant",
  virement: "Virement",
  en_ligne: "En ligne",
  autre: "Autre",
};

export const PAYMENT_KIND_LABELS: Record<PaymentKind, string> = {
  acompte: "Acompte",
  solde: "Solde",
  remboursement: "Remboursement",
};

/** Taux de taxes du Québec, en millièmes de pour cent (5 % → 5000 ; 9,975 % → 9975). */
export const GST_RATE_MILLI = 5000;
export const QST_RATE_MILLI = 9975;

/** Ordre naturel du cycle de vie (pour les onglets et le bouton « étape suivante »). */
export const APPOINTMENT_STATUS_ORDER: AppointmentStatus[] = [
  "devis",
  "reserve",
  "confirme",
  "vehicule_recu",
  "en_cours",
  "termine",
  "pret",
  "recupere",
  "annule",
  "non_presente",
];

/** Regroupements pour la liste des travaux (onglets). */
export const APPOINTMENT_STATUS_GROUPS: Array<{
  key: string;
  label: string;
  statuses: AppointmentStatus[];
}> = [
  { key: "devis", label: "Devis", statuses: ["devis"] },
  { key: "planifies", label: "Planifiés", statuses: ["reserve", "confirme"] },
  {
    key: "en_atelier",
    label: "En atelier",
    statuses: ["vehicule_recu", "en_cours", "termine", "pret"],
  },
  { key: "termines", label: "Récupérés", statuses: ["recupere"] },
  { key: "annules", label: "Annulés", statuses: ["annule", "non_presente"] },
];

/**
 * Parcours normal d'un travail, du rendez-vous pris au véhicule rendu. C'est la
 * frise affichée sur la fiche : un technicien y tape l'étape atteinte.
 */
export const APPOINTMENT_FLOW: AppointmentStatus[] = [
  "reserve",
  "confirme",
  "vehicule_recu",
  "en_cours",
  "termine",
  "pret",
  "recupere",
];

/** Verbe d'action pour atteindre une étape (« Étape suivante : … » devient un ordre clair). */
export const APPOINTMENT_STEP_ACTIONS: Record<AppointmentStatus, string> = {
  devis: "Repasser en devis",
  reserve: "Réserver",
  confirme: "Confirmer",
  vehicule_recu: "Véhicule reçu",
  en_cours: "Commencer le travail",
  termine: "Travail terminé",
  pret: "Prêt à récupérer",
  recupere: "Véhicule récupéré",
  annule: "Annuler",
  non_presente: "Client non présenté",
};

/** Étape suivante « normale » du cycle (null s'il n'y en a pas). */
export function nextAppointmentStatus(s: AppointmentStatus): AppointmentStatus | null {
  const i = APPOINTMENT_FLOW.indexOf(s);
  return i >= 0 && i < APPOINTMENT_FLOW.length - 1
    ? APPOINTMENT_FLOW[i + 1]!
    : s === "devis"
      ? "reserve"
      : null;
}

export function vehicleLabel(v: Pick<Vehicle, "make" | "model" | "year">): string {
  return [v.year ?? null, v.make, v.model].filter(Boolean).join(" ");
}

// ---- Schémas ----

export const clientTypeSchema = z.enum(["particulier", "concessionnaire", "flotte"]);
export const leadSourceSchema = z.enum([
  "urable",
  "site_web",
  "chatbot",
  "appel_ia",
  "appel_direct",
  "campagne_sms",
  "reference",
  "concessionnaire",
  "reseaux_sociaux",
  "autre",
]);
export const appointmentStatusSchema = z.enum([
  "devis",
  "reserve",
  "confirme",
  "vehicule_recu",
  "en_cours",
  "termine",
  "pret",
  "recupere",
  "annule",
  "non_presente",
]);

const optionalText = z.string().trim().max(500).nullable().optional();

export const createClientSchema = z.object({
  type: clientTypeSchema.default("particulier"),
  name: z.string().trim().min(1).max(160),
  phone: optionalText,
  email: z.union([z.string().trim().email(), z.literal(""), z.null()]).optional(),
  address: optionalText,
  city: optionalText,
  postalCode: optionalText,
  smsConsent: z.boolean().default(false),
  source: leadSourceSchema.default("autre"),
  notes: z.string().max(5000).nullable().optional(),
});
export type CreateClientInput = z.infer<typeof createClientSchema>;
export const updateClientSchema = createClientSchema.partial();

export const createVehicleSchema = z.object({
  pricingClass: z.enum(VEHICLE_CLASSES).nullable().optional(),
  make: z.string().trim().min(1).max(80),
  model: z.string().trim().min(1).max(80),
  year: z.number().int().min(1950).max(2100).nullable().optional(),
  vin: z.string().trim().max(17).nullable().optional(),
  plate: z.string().trim().max(16).nullable().optional(),
  mileage: z.number().int().min(0).nullable().optional(),
  color: z.string().trim().max(40).nullable().optional(),
  notes: z.string().max(5000).nullable().optional(),
});
export type CreateVehicleInput = z.infer<typeof createVehicleSchema>;
export const updateVehicleSchema = createVehicleSchema.partial();

/** Couleur « #rrggbb » ; vide ou absente = couleur par défaut. */
export const hexColorSchema = z
  .string()
  .trim()
  .refine((v) => v === "" || isHexColor(v), "couleur_invalide")
  .transform((v) => (v === "" ? null : v.toLowerCase()))
  .nullable();

export const createCategorySchema = z.object({
  name: z.string().trim().min(1).max(120),
  onlineVisible: z.boolean().default(true),
  position: z.number().int().min(0).optional(),
  color: hexColorSchema.optional(),
});
export const updateCategorySchema = createCategorySchema.partial();

export const createServiceSchema = z.object({
  warrantyEligible: z.boolean().optional(),
  vehicleClasses: z.array(z.enum(VEHICLE_CLASSES)).max(6).nullable().optional(),
  variantGroup: z.string().trim().max(120).nullable().optional(),
  categoryId: z.string().min(1),
  name: z.string().trim().min(1).max(160),
  description: z.string().max(2000).nullable().optional(),
  priceCents: z.number().int().min(0).nullable().optional(),
  quoteOnly: z.boolean().default(false),
  durationMin: z
    .number()
    .int()
    .min(5)
    .max(24 * 60)
    .nullable()
    .default(60),
  onlineBookable: z.boolean().default(true),
  publicVisible: z.boolean().default(true),
  aiPriceVisible: z.boolean().default(true),
  active: z.boolean().default(true),
  position: z.number().int().min(0).optional(),
  laborMin: z
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .nullable()
    .optional(),
  guideSlugs: z.array(z.string().trim().min(1).max(120)).max(50).optional(),
  bundleServiceIds: z.array(z.string().min(1)).max(30).optional(),
  pricingUnit: z.enum(["fixed", "hourly"]).optional(),
  maxVehicleAgeYears: z.number().int().min(0).max(50).nullable().optional(),
  maxDistanceKm: z.number().int().min(1).max(500).nullable().optional(),
});
export type CreateServiceInput = z.infer<typeof createServiceSchema>;
export const updateServiceSchema = createServiceSchema.partial();

/** Liaisons d'un service (et de ses déclinaisons) : la liste remplace l'existante. */
export const serviceLinksSchema = z.object({
  items: z
    .array(
      z.object({
        linkedServiceId: z.string().min(1),
        kind: z.enum(["obligatoire", "recommande"]),
        note: z.string().trim().max(200).nullable().optional(),
        vehicleClasses: z.array(z.enum(VEHICLE_CLASSES)).max(6).optional(),
      }),
    )
    .max(50),
});
export type ServiceLinksInput = z.infer<typeof serviceLinksSchema>;

/** Numéro bloqué : Léo ne prend aucun rendez-vous, la réservation en ligne est refusée. */
export interface BlockedNumber {
  id: string;
  /** E.164 (+14185550123). */
  phone: string;
  reason: string | null;
  createdAt: number | string;
  createdByName: string | null;
}
export const createBlockedNumberSchema = z.object({
  phone: z.string().trim().min(7).max(40),
  reason: z.string().trim().max(300).nullable().optional(),
});
export type CreateBlockedNumberInput = z.infer<typeof createBlockedNumberSchema>;

/** Âge d'un véhicule en années pour une limite « tant d'années ou moins » : l'année modèle compte pour l'année courante. */
export function vehicleAgeYears(year: number, now = new Date()): number {
  return Math.max(0, now.getFullYear() - year);
}

/** Le véhicule dépasse-t-il l'âge maximal du service ? Inconnu (année absente) = non. */
export function vehicleTooOldFor(
  service: Pick<Service, "maxVehicleAgeYears">,
  year: number | null | undefined,
  now = new Date(),
): boolean {
  if (service.maxVehicleAgeYears === null || service.maxVehicleAgeYears === undefined) return false;
  if (!year) return false;
  return vehicleAgeYears(year, now) > service.maxVehicleAgeYears;
}

export const createDiscountRuleSchema = z.object({
  name: z.string().trim().min(1).max(120),
  percent: z.number().int().min(1).max(100),
  triggerServiceIds: z.array(z.string().min(1)).min(1).max(50),
  targetServiceIds: z.array(z.string().min(1)).min(1).max(50),
  active: z.boolean().default(true),
  position: z.number().int().min(0).optional(),
});
export type CreateDiscountRuleInput = z.infer<typeof createDiscountRuleSchema>;
export const updateDiscountRuleSchema = createDiscountRuleSchema.partial();

export const orderSchema = z.object({ ids: z.array(z.string().min(1)).min(1) });

export const createProductSchema = z.object({
  name: z.string().trim().min(1).max(160),
  supplier: optionalText,
  unit: z.string().trim().min(1).max(20).default("unité"),
  unitCostCents: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
  position: z.number().int().min(0).optional(),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;
export const updateProductSchema = createProductSchema.partial();

/** Produits consommés par un service : la liste remplace l'existante. */
export const serviceProductsSchema = z.object({
  items: z
    .array(
      z.object({
        productId: z.string().min(1),
        quantity: z.number().positive().max(1_000_000),
      }),
    )
    .max(200),
});
export type ServiceProductsInput = z.infer<typeof serviceProductsSchema>;

const clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const weeklyHoursSchema = z
  .record(
    z.enum(["1", "2", "3", "4", "5", "6", "7"]),
    z
      .array(
        z
          .object({ open: clockTimeSchema, close: clockTimeSchema })
          .refine((v) => v.close > v.open, "La fin doit suivre le début"),
      )
      .max(4)
      .refine((rows) => {
        const ordered = [...rows].sort((a, b) => a.open.localeCompare(b.open));
        return ordered.every((r, i) => i === 0 || ordered[i - 1]!.close <= r.open);
      }, "Les plages se chevauchent"),
  )
  .nullable();
export const createTechnicianSchema = z.object({
  userId: z.string().min(1).nullable().optional(),
  displayName: z.string().trim().min(1).max(120),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default("#f5b800"),
  phone: optionalText,
  hourlyRateCents: z.number().int().min(0).nullable().optional(),
  active: z.boolean().default(true),
  position: z.number().int().min(0).optional(),
  workdays: z.array(z.number().int().min(1).max(7)).max(7).optional(),
  weeklyHours: weeklyHoursSchema.optional(),
});
export const updateTechnicianSchema = createTechnicianSchema.partial();

const dayKeySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date AAAA-MM-JJ attendue");
export const createTimeOffSchema = z
  .object({
    fromDate: dayKeySchema,
    toDate: dayKeySchema,
    fromTime: clockTimeSchema.nullable().optional(),
    toTime: clockTimeSchema.nullable().optional(),
    label: z.string().trim().max(120).nullable().optional(),
  })
  .refine((v) => v.toDate >= v.fromDate, { message: "la fin précède le début", path: ["toDate"] })
  .refine(
    (v) =>
      Boolean(v.fromTime) === Boolean(v.toTime) &&
      (!v.fromTime || !v.toTime || v.toDate > v.fromDate || v.toTime > v.fromTime),
    { message: "Heures de début et de fin requises, dans l’ordre", path: ["toTime"] },
  );
export type CreateTimeOffInput = z.infer<typeof createTimeOffSchema>;

export const appointmentLineSchema = z.object({
  serviceId: z.string().min(1).nullable().optional(),
  label: z.string().trim().min(1).max(200),
  priceCents: z.number().int().min(0).default(0),
  durationMin: z.number().int().min(0).default(0),
  quantity: z.number().int().min(1).default(1),
  discountPct: z.number().int().min(0).max(100).default(0),
  discountLabel: z.string().trim().max(120).nullable().optional(),
});
export type AppointmentLineInput = z.infer<typeof appointmentLineSchema>;

/** Date ISO acceptée par l'API pour les débuts/fins. */
const isoDate = z.string().datetime({ offset: true });

export const createAppointmentSchema = z.object({
  clientId: z.string().min(1),
  vehicleId: z.string().min(1).nullable().optional(),
  technicianId: z.string().min(1).nullable().optional(),
  status: appointmentStatusSchema.default("reserve"),
  startAt: isoDate,
  /** Si absent : début + somme des durées des lignes (minimum 30 min). */
  endAt: isoDate.optional(),
  title: z.string().trim().max(200).optional(),
  notes: z.string().max(5000).nullable().optional(),
  clientInstructions: z.string().trim().max(1000).nullable().optional(),
  source: leadSourceSchema.default("appel_direct"),
  depositCents: z.number().int().min(0).default(0),
  paidCents: z.number().int().min(0).default(0),
  taxExempt: z.boolean().default(false),
  color: hexColorSchema.optional(),
  lines: z.array(appointmentLineSchema).default([]),
  /**
   * Dérogation explicite : planifier un rendez-vous qui chevauche la pause de
   * midi ou une fermeture. Sans elle, l'API refuse (409 `internal_block`).
   */
  allowInternalBlock: z.boolean().optional(),
});
export type CreateAppointmentInput = z.infer<typeof createAppointmentSchema>;
export const updateAppointmentSchema = createAppointmentSchema.partial().extend({
  /**
   * Rendez-vous déplacé : prévenir le client du nouvel horaire par texto
   * (défaut). Faux quand il est déjà au courant (déplacé à sa demande au téléphone).
   */
  notifyClient: z.boolean().optional(),
});
export const setAppointmentStatusSchema = z.object({ status: appointmentStatusSchema });

export const paymentMethodSchema = z.enum(["carte", "comptant", "virement", "en_ligne", "autre"]);
export const paymentKindSchema = z.enum(["acompte", "solde", "remboursement"]);
export const createPaymentSchema = z.object({
  /** Toujours positif ; le type « remboursement » indique le sens. */
  amountCents: z.number().int().min(1),
  method: paymentMethodSchema.default("carte"),
  kind: paymentKindSchema.default("solde"),
  /** Par défaut : maintenant. */
  paidAt: isoDate.optional(),
  reference: z.string().trim().max(120).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type CreatePaymentInput = z.infer<typeof createPaymentSchema>;

/** Montant d'une ligne après rabais : prix × quantité × (100 − rabais) / 100, arrondi au cent. */
export function lineTotalCents(l: {
  priceCents?: number;
  quantity?: number;
  discountPct?: number;
}): number {
  const pct = Math.min(100, Math.max(0, l.discountPct ?? 0));
  return Math.round(((l.priceCents ?? 0) * (l.quantity ?? 1) * (100 - pct)) / 100);
}

/** Total et durée d'un rendez-vous d'après ses lignes (rabais déduits). */
export function sumLines(
  lines: Array<
    Pick<AppointmentLineInput, "priceCents" | "durationMin" | "quantity"> & { discountPct?: number }
  >,
) {
  return lines.reduce(
    (acc, l) => ({
      totalCents: acc.totalCents + lineTotalCents(l),
      durationMin: acc.durationMin + (l.durationMin ?? 0) * (l.quantity ?? 1),
    }),
    { totalCents: 0, durationMin: 0 },
  );
}

/** Ce que les moteurs de rabais et de forfait ont besoin de lire sur une ligne. */
export interface DiscountableLine {
  serviceId: string | null;
  priceCents: number;
  quantity: number;
  discountPct: number;
  discountLabel: string | null;
}

/**
 * Applique les règles de rabais automatiques. Une ligne dont le rabais a été
 * saisi à la main (pourcentage sans étiquette) n'est jamais touchée ; une ligne
 * marquée par une règle est recalculée à chaque passage — le rabais tombe si
 * le déclencheur a quitté le rendez-vous. Plusieurs règles : la plus généreuse.
 * Fonction pure, partagée par l'API (source de vérité) et l'écran (aperçu).
 */
export function applyDiscountRules<T extends DiscountableLine>(
  lines: T[],
  rules: DiscountRule[],
): T[] {
  const present = new Set(lines.map((l) => l.serviceId).filter((id): id is string => Boolean(id)));
  return lines.map((line) => {
    const manual = line.discountPct > 0 && !line.discountLabel;
    if (manual || !line.serviceId) return line;
    let best: DiscountRule | null = null;
    for (const r of rules) {
      if (!r.active || !r.targetServiceIds.includes(line.serviceId)) continue;
      if (!r.triggerServiceIds.some((id) => id !== line.serviceId && present.has(id))) continue;
      if (!best || r.percent > best.percent) best = r;
    }
    if (best) {
      return line.discountPct === best.percent && line.discountLabel === best.name
        ? line
        : { ...line, discountPct: best.percent, discountLabel: best.name };
    }
    return line.discountLabel ? { ...line, discountPct: 0, discountLabel: null } : line;
  });
}

export interface BundleSuggestion {
  bundle: Service;
  /** Ce que coûtent les services séparés (une unité chacun, rabais déduits). */
  separateCents: number;
  savingsCents: number;
  /** Indices des lignes que le forfait remplace (une par service inclus). */
  lineIndexes: number[];
}

/** Clé de famille d'un service : son groupe de variantes, sinon lui-même. */
export function serviceFamilyKey(s: Pick<Service, "id" | "variantGroup">): string {
  return s.variantGroup ? `g:${s.variantGroup}` : `s:${s.id}`;
}

/** Les déclinaisons d'une famille (le service lui-même s'il n'est pas décliné). */
export function familyVariants<T extends Pick<Service, "id" | "variantGroup">>(
  s: Pick<Service, "id" | "variantGroup">,
  all: T[],
): T[] {
  const key = serviceFamilyKey(s);
  const variants = all.filter((x) => serviceFamilyKey(x) === key);
  return variants.length ? variants : [];
}

/**
 * La déclinaison d'une famille qui convient à un véhicule : celle de sa classe,
 * sinon une déclinaison sans classe, sinon la première active. Null si la
 * famille n'a aucune déclinaison active.
 */
export function variantForVehicle<T extends Service>(
  family: T[],
  vehicleClass?: VehicleClass | null,
): T | null {
  const active = family.filter((s) => s.active);
  if (!active.length) return null;
  if (vehicleClass) {
    const exact = active.find((s) => s.vehicleClasses?.includes(vehicleClass));
    if (exact) return exact;
  }
  return active.find((s) => !s.vehicleClasses?.length) ?? (vehicleClass ? null : active[0]!);
}

/**
 * Forfaits proposables : chaque service inclus dans le forfait (ou l'une de
 * ses déclinaisons) est au rendez-vous, le forfait n'y est pas encore, et sa
 * déclinaison pour le véhicule coûte moins cher que la somme des lignes
 * qu'elle remplace. Du plus avantageux au moins avantageux.
 */
export function suggestBundles(
  lines: DiscountableLine[],
  services: Service[],
  vehicleClass?: VehicleClass | null,
): BundleSuggestion[] {
  const out: BundleSuggestion[] = [];
  const byId = new Map(services.map((s) => [s.id, s]));
  const familyOf = (id: string | null) =>
    id && byId.get(id) ? serviceFamilyKey(byId.get(id)!) : null;
  const onBoard = new Set(lines.map((l) => familyOf(l.serviceId)));
  const seenFamilies = new Set<string>();
  for (const candidate of services) {
    if (!candidate.active || candidate.bundleServiceIds.length < 2) continue;
    const familyKey = serviceFamilyKey(candidate);
    if (seenFamilies.has(familyKey) || onBoard.has(familyKey)) continue;
    seenFamilies.add(familyKey);
    // Aucune déclinaison du forfait pour ce véhicule (pas de prix VUS, par
    // exemple) : rien à proposer, jamais le prix d'un autre format.
    const bundle = variantForVehicle(familyVariants(candidate, services), vehicleClass);
    if (!bundle || bundle.priceCents === null || bundle.quoteOnly) continue;
    const indexes: number[] = [];
    let separate = 0;
    let complete = true;
    for (const id of bundle.bundleServiceIds) {
      const wanted = familyOf(id) ?? `s:${id}`;
      const index = lines.findIndex(
        (l, i) => !indexes.includes(i) && (l.serviceId === id || familyOf(l.serviceId) === wanted),
      );
      if (index < 0) {
        complete = false;
        break;
      }
      indexes.push(index);
      separate += lineTotalCents({ ...lines[index]!, quantity: 1 });
    }
    if (!complete) continue;
    const savings = separate - bundle.priceCents;
    if (savings <= 0) continue;
    out.push({ bundle, separateCents: separate, savingsCents: savings, lineIndexes: indexes });
  }
  return out.sort((a, b) => b.savingsCents - a.savingsCents);
}

/** Une liaison vaut-elle pour ce véhicule ? Sans classe réglée, toujours. */
export function linkAppliesTo(link: ServiceLink, vehicleClass?: VehicleClass | null): boolean {
  if (!link.vehicleClasses.length) return true;
  return Boolean(vehicleClass && link.vehicleClasses.includes(vehicleClass));
}

export interface LinkedServiceSuggestion {
  link: ServiceLink;
  /** Service d'origine de la liaison, tel que choisi. */
  from: Service;
  /** Déclinaison à ajouter pour le véhicule. */
  service: Service;
}

/**
 * Services liés à une sélection : les obligatoires manquants (à ajouter) et
 * les recommandés absents (à proposer), résolus à la déclinaison du véhicule,
 * sans doublon de famille. Une liaison vers une famille déjà choisie ne
 * propose rien.
 */
export function linkedServicesFor(
  selected: Service[],
  all: Service[],
  vehicleClass?: VehicleClass | null,
): { required: LinkedServiceSuggestion[]; recommended: LinkedServiceSuggestion[] } {
  const chosen = new Set(selected.map(serviceFamilyKey));
  const byId = new Map(all.map((s) => [s.id, s]));
  const required: LinkedServiceSuggestion[] = [];
  const recommended: LinkedServiceSuggestion[] = [];
  const added = new Set<string>();
  for (const from of selected) {
    for (const link of from.links ?? []) {
      if (!linkAppliesTo(link, vehicleClass)) continue;
      const target = byId.get(link.linkedServiceId);
      if (!target) continue;
      const key = serviceFamilyKey(target);
      if (chosen.has(key) || added.has(key)) continue;
      const family = familyVariants(target, all);
      const service = variantForQuantity(from, family) ?? variantForVehicle(family, vehicleClass);
      if (!service) continue;
      added.add(key);
      (link.kind === "obligatoire" ? required : recommended).push({ link, from, service });
    }
  }
  return { required, recommended };
}

/**
 * « Traitement des cuirs — 5 sièges » appelle le shampoing des 5 sièges, pas
 * celui d'un seul : la déclinaison liée suit la quantité du service choisi
 * (sièges, tapis). Null si l'un des deux n'est pas décliné par quantité.
 */
function variantForQuantity<T extends Service>(from: Service, family: T[]): T | null {
  const wanted = parseServiceVariant(from);
  if (
    (wanted.kind !== "seats" && wanted.kind !== "carpets") ||
    wanted.count === null ||
    wanted.extra
  )
    return null;
  return (
    family.find((s) => {
      if (!s.active) return false;
      const p = parseServiceVariant(s);
      return p.kind === wanted.kind && p.count === wanted.count && !p.extra;
    }) ?? null
  );
}

/** Heures estimées d'un service au tarif horaire, d'après sa durée (au moins une). */
export function hourlyEstimateHours(service: Pick<Service, "durationMin">): number {
  return Math.max(1, Math.ceil((service.durationMin ?? 60) / 60));
}

export function formatCad(cents: number): string {
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(cents / 100);
}

// ---- Variantes de service ----

/**
 * Le catalogue importé décline un même service en variantes suffixées
 * « — 1 tapis », « — SUV »… Les lister à plat donne dix entrées « Shampoing »
 * qu'il faut lire une à une. `groupServiceVariants` rassemble ces déclinaisons
 * sous leur nom commun : l'écran montre un service, puis le choix de variante.
 */
export interface ServiceVariantGroup {
  /** `variantGroup` de la source, sinon l'identifiant du service seul. */
  key: string;
  /** Nom sans le suffixe de variante. */
  name: string;
  categoryId: string;
  /** Variantes, dans l'ordre du catalogue ; une seule = service simple. */
  variants: Service[];
}

/** Suffixe de variante (« 2 tapis », « SUV ») ; vide si le service n'en a pas. */
export function variantLabel(s: Service): string {
  const parts = s.name.split(" — ");
  return parts.length > 1 ? parts.slice(1).join(" — ").trim() : "";
}

/** Nom sans le suffixe de variante. */
export function serviceBaseName(s: Service): string {
  return s.name.split(" — ")[0]!.trim();
}

/**
 * Groupe les services par variante en conservant l'ordre du catalogue. Un
 * groupe qui n'aurait qu'une variante reste un service ordinaire : pas de
 * sélecteur pour un choix unique.
 */
export function groupServiceVariants(services: Service[]): ServiceVariantGroup[] {
  const groups: ServiceVariantGroup[] = [];
  const index = new Map<string, ServiceVariantGroup>();
  for (const s of services) {
    // Sans suffixe « — », deux services d'un même groupe source restent
    // distincts : on ne fusionne que ce qui se lit comme une déclinaison.
    const key = s.variantGroup && variantLabel(s) ? `g:${s.variantGroup}` : `s:${s.id}`;
    const existing = index.get(key);
    if (existing) {
      existing.variants.push(s);
      continue;
    }
    const group: ServiceVariantGroup = {
      key,
      name: serviceBaseName(s),
      categoryId: s.categoryId,
      variants: [s],
    };
    index.set(key, group);
    groups.push(group);
  }
  return groups;
}

/** Minuscules sans accents : « Décontamination » se trouve en tapant « deconta ». */
export function normalizeSearch(value: string): string {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/** Le service répond-il à la recherche ? Nom, description et catégorie. */
export function serviceMatches(s: Service, query: string, categoryName?: string): boolean {
  const q = normalizeSearch(query);
  if (!q) return true;
  const haystack = normalizeSearch([s.name, s.description ?? "", categoryName ?? ""].join(" "));
  return q.split(/\s+/).every((word) => haystack.includes(word));
}

// ---- Axes de déclinaison ----

/**
 * Ce qui fait varier le prix d'un service décliné : le type de véhicule, le
 * nombre de sièges, le nombre de tapis — les trois familles qui reviennent
 * dans tout le catalogue — ou une déclinaison libre (« 30 min », « Avec
 * sablage »…). L'axe se lit dans le suffixe du nom : aucune colonne de plus.
 */
export type VariantKind = "vehicle" | "seats" | "carpets" | "custom";

export const VARIANT_KIND_LABELS: Record<VariantKind, string> = {
  vehicle: "Par type de véhicule",
  seats: "Par nombre de sièges",
  carpets: "Par nombre de tapis",
  custom: "Déclinaisons libres",
};

export interface ParsedVariant {
  kind: VariantKind;
  /** Suffixe tel qu'écrit dans le nom (vide pour un service simple). */
  label: string;
  /** Axe véhicule : classes visées (une seule, sauf libellé composé « Sedan/Vus »). */
  vehicleClasses: VehicleClass[];
  /** Axes sièges/tapis : quantité ; « Ajout 1 siège » donne 1 avec `extra`. */
  count: number | null;
  /** Ligne d'appoint (« Ajout 1 tapis ») : ajoutée à une quantité, jamais seule. */
  extra: boolean;
}

const COUNT_RE = /^(\d+)\s+(tapis|si[eè]ges?)$/i;
const EXTRA_RE = /^ajout\s+(\d+)\s+(tapis|si[eè]ges?)$/i;

/** Classe de véhicule dont le libellé français (accents et casse ignorés) correspond. */
function vehicleClassByLabel(label: string): VehicleClass | null {
  const wanted = normalizeSearch(label);
  for (const c of VEHICLE_CLASSES) {
    if (normalizeSearch(VEHICLE_CLASS_LABELS[c]) === wanted) return c;
  }
  return null;
}

/**
 * Lit une déclinaison dans son libellé — et, à défaut, dans les classes de
 * véhicule enregistrées sur le service (un libellé importé comme « Truck »
 * porte déjà `vehicleClasses: ["truck"]`).
 */
export function parseVariantLabel(
  label: string,
  vehicleClasses?: VehicleClass[] | null,
): ParsedVariant {
  const clean = label.trim();
  const base: ParsedVariant = {
    kind: "custom",
    label: clean,
    vehicleClasses: [],
    count: null,
    extra: false,
  };
  if (!clean) return base;
  const extra = EXTRA_RE.exec(clean);
  if (extra) {
    return {
      ...base,
      kind: /tapis/i.test(extra[2]!) ? "carpets" : "seats",
      count: Number(extra[1]),
      extra: true,
    };
  }
  const counted = COUNT_RE.exec(clean);
  if (counted) {
    return {
      ...base,
      kind: /tapis/i.test(counted[2]!) ? "carpets" : "seats",
      count: Number(counted[1]),
    };
  }
  const imported = vehicleClassesForVariant(clean);
  if (imported.length) return { ...base, kind: "vehicle", vehicleClasses: imported };
  const french = vehicleClassByLabel(clean);
  if (french) return { ...base, kind: "vehicle", vehicleClasses: [french] };
  if (vehicleClasses && vehicleClasses.length === 1) {
    return { ...base, kind: "vehicle", vehicleClasses: [...vehicleClasses] };
  }
  return base;
}

/** Déclinaison d'un service telle qu'elle se lit dans son nom et ses classes. */
export function parseServiceVariant(s: Service): ParsedVariant {
  return parseVariantLabel(variantLabel(s), s.vehicleClasses);
}

/**
 * Axe d'un groupe : celui de la majorité de ses variantes. Un groupe mêlé
 * (« 5 sièges », « Sièges et tapis ») reste lisible ligne par ligne : chaque
 * pastille garde sa propre lecture, seul le résumé devient « libre ».
 */
export function variantAxis(variants: Service[]): VariantKind {
  if (variants.length < 2) return "custom";
  const tally = new Map<VariantKind, number>();
  for (const v of variants) {
    const kind = parseServiceVariant(v).kind;
    tally.set(kind, (tally.get(kind) ?? 0) + 1);
  }
  let best: VariantKind = "custom";
  let bestCount = 0;
  for (const kind of ["vehicle", "seats", "carpets"] as const) {
    const n = tally.get(kind) ?? 0;
    if (n > bestCount) {
      best = kind;
      bestCount = n;
    }
  }
  return bestCount * 2 >= variants.length ? best : "custom";
}

/** Libellé canonique d'une déclinaison créée depuis l'écran. */
export function variantLabelFor(
  kind: Exclude<VariantKind, "custom">,
  value: VehicleClass | number,
  extra = false,
): string {
  if (kind === "vehicle") return VEHICLE_CLASS_LABELS[value as VehicleClass];
  const n = Number(value);
  const unit = kind === "carpets" ? "tapis" : n > 1 && !extra ? "sièges" : "siège";
  return extra ? `Ajout ${n} ${unit}` : `${n} ${unit}`;
}

/** « Shampoing tapis — 2 tapis » ; sans suffixe, le nom seul. */
export function serviceVariantName(baseName: string, label: string): string {
  const base = baseName.trim();
  const suffix = label.trim();
  return suffix ? `${base} — ${suffix}` : base;
}

/** Quantités proposées d'office pour les sièges et les tapis. */
export const DEFAULT_VARIANT_COUNTS = [1, 2, 3, 5] as const;

/**
 * Rang d'une déclinaison dans l'ordre naturel de son axe : du coupé au gros
 * camion, du plus petit nombre au plus grand, l'appoint en dernier. Les
 * déclinaisons libres gardent l'ordre du catalogue (rang neutre).
 */
export function variantRank(p: ParsedVariant): number {
  if (p.kind === "vehicle") {
    const first = p.vehicleClasses[0];
    return first ? VEHICLE_CLASSES.indexOf(first) : VEHICLE_CLASSES.length;
  }
  if (p.kind === "seats" || p.kind === "carpets") {
    return p.extra ? 1000 + (p.count ?? 0) : (p.count ?? 0);
  }
  return 0;
}

/** Variantes triées dans l'ordre naturel de leur axe (stable pour les libres). */
export function sortVariants<T extends Service>(variants: T[]): T[] {
  return variants
    .map((v, i) => ({ v, i, rank: variantRank(parseServiceVariant(v)) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.v);
}

// ---- Catalogue des véhicules (recherche tolérante et noms officiels) ----
export * from "./vehicules";
export { VEHICLE_CATALOG } from "./vehicules-catalogue";
